import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type MsisdnMode = 'facade' | 'custom';

export interface MsisdnConfigValue {
  mode: MsisdnMode;
  url: string;
}

interface MsisdnConfigProps {
  value: MsisdnConfigValue;
  onChange?: (value: MsisdnConfigValue) => void;
  disabled?: boolean;
  /** Externer Validierungsfehler (z.B. nach Submit-Versuch). */
  error?: string;
}

/**
 * Stable validation codes returned by {@link validateMsisdnUrl}. The caller maps these to
 * a localised message (i18n `routing:msisdn.validation.<code>`) — keeping this module-level
 * helper free of display strings so the language can follow the UI.
 */
export type MsisdnValidationCode =
  | 'nonHttps'
  | 'localhost'
  | 'privateIp'
  | 'linkLocal'
  | 'invalidUrl';

/**
 * Client-seitige SSRF-Validierung fuer eine Merchant-eigene MSISDN-URL
 * (Spec 0128 / 0216 §6.3). Gibt einen stabilen Code zurueck (null = gueltig);
 * die Anzeige-Meldung loest der Aufrufer ueber i18n auf.
 */
export function validateMsisdnUrl(url: string): MsisdnValidationCode | null {
  if (!url) return null;
  if (!url.startsWith('https://')) return 'nonHttps';
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return 'localhost';
    if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(host)) return 'privateIp';
    if (/^169\.254\./.test(host)) return 'linkLocal';
    return null;
  } catch {
    return 'invalidUrl';
  }
}

/**
 * Card mit Mode-Toggle (Facade-Standard vs. Custom-URL) + URL-Input.
 * Voll controlled — keinen internen State, keine Side-Effects.
 */
export function MsisdnConfig({ value, onChange, disabled = false, error }: MsisdnConfigProps) {
  const { t } = useTranslation('routing');
  const interactive = !disabled && !!onChange;

  const handleModeChange = (mode: MsisdnMode) => {
    if (!interactive || !onChange) return;
    onChange({ mode, url: mode === 'custom' ? value.url : '' });
  };

  const handleUrlChange = (url: string) => {
    if (!interactive || !onChange) return;
    onChange({ mode: value.mode, url });
  };

  return (
    <div className="space-y-3">
      <fieldset className="space-y-2" disabled={!interactive}>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="msisdn-mode"
            value="facade"
            checked={value.mode === 'facade'}
            onChange={() => handleModeChange('facade')}
            className="mt-0.5"
          />
          <span>{t('msisdn.modeFacade')}</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="msisdn-mode"
            value="custom"
            checked={value.mode === 'custom'}
            onChange={() => handleModeChange('custom')}
            className="mt-0.5"
          />
          <span>{t('msisdn.modeCustom')}</span>
        </label>
      </fieldset>

      {value.mode === 'custom' && (
        <div className="space-y-1.5 pl-6">
          <Label htmlFor="msisdn-url">{t('msisdn.urlLabel')}</Label>
          <Input
            id="msisdn-url"
            value={value.url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://example.com/msisdn"
            disabled={!interactive}
            aria-invalid={!!error}
          />
          {error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{t('msisdn.urlHint')}</p>
          )}
        </div>
      )}
    </div>
  );
}
